/**
 * Justvero cart add-on grouping — v3.1.
 *
 * Handles cascade removal (parent → add-ons), quantity sync, and orphan cleanup
 * for the priced add-on architecture (see docs/product/priced-add-on-contract.md).
 *
 * Works on BOTH cart drawer and cart page — uses /cart.js API for item lookup
 * instead of DOM row indexing (avoids issues when both contexts are in the DOM).
 */
(function () {
  'use strict';

  let cleaning = false;
  let suppressObserver = false;

  async function cleanOrphansFromCart() {
    if (cleaning || suppressObserver) return;
    cleaning = true;

    try {
      const resp = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await resp.json();
      if (!cart.items || cart.items.length === 0) { cleaning = false; return; }

      const parentConfigIds = new Set();
      const parentQuantities = new Map();
      const addonItems = [];

      for (const item of cart.items) {
        const props = item.properties || {};
        const role = props._role;
        const configId = props._configuration_id;
        if (!configId) continue;

        if (role === 'parent') {
          parentConfigIds.add(configId);
          parentQuantities.set(configId, item.quantity);
        } else if (role === 'addon') {
          addonItems.push({ key: item.key, configId, quantity: item.quantity });
        }
      }

      const updates = {};
      let needsUpdate = false;

      for (const addon of addonItems) {
        if (!parentConfigIds.has(addon.configId)) {
          updates[addon.key] = 0;
          needsUpdate = true;
        } else {
          const parentQty = parentQuantities.get(addon.configId);
          if (parentQty !== undefined && addon.quantity !== parentQty) {
            updates[addon.key] = parentQty;
            needsUpdate = true;
          }
        }
      }

      if (!needsUpdate) { cleaning = false; return; }

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });

      await rerenderCartSections();
    } catch (e) {
      console.warn('[jv-cart-addons] Clean error:', e);
    } finally {
      cleaning = false;
    }
  }

  async function rerenderCartSections() {
    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component').forEach(c => {
      if (c.dataset?.sectionId) sectionIds.add(c.dataset.sectionId);
    });

    if (sectionIds.size === 0) return;

    const sectionsParam = [...sectionIds].join(',');
    const sectionsResp = await fetch(
      `${window.location.pathname}?sections=${sectionsParam}`
    );
    const sectionsData = await sectionsResp.json();
    suppressObserver = true;
    for (const [sectionId, html] of Object.entries(sectionsData)) {
      const container = document.getElementById(`shopify-section-${sectionId}`);
      if (container) container.innerHTML = html;
    }
    requestAnimationFrame(() => { suppressObserver = false; });
  }

  /**
   * Fetch interceptor: cascade parent removal/quantity to add-ons.
   *
   * Fetches /cart.js just-in-time (not cached) to reliably identify
   * the changed item. Returns a Promise chain so the caller gets
   * the /cart/update.js response in the same shape as /cart/change.js.
   */
  const _fetch = window.fetch;
  let cascading = false;

  window.fetch = function (url, options) {
    const urlStr = typeof url === 'string' ? url : url?.url || '';
    if (!urlStr.includes('/cart/change') || cascading) {
      return _fetch.apply(this, arguments);
    }

    let body;
    try { body = options?.body ? JSON.parse(options.body) : null; } catch { body = null; }
    if (!body || body.quantity === undefined) return _fetch.apply(this, arguments);

    const newQuantity = body.quantity;
    const lineIndex = body.line;
    if (!lineIndex) return _fetch.apply(this, arguments);

    cascading = true;

    return _fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(resp => resp.json())
      .then(cart => {
        const changedItem = cart.items[lineIndex - 1];
        if (!changedItem) {
          cascading = false;
          return _fetch.apply(null, [url, options]);
        }

        const props = changedItem.properties || {};
        const configId = props._configuration_id;
        const role = props._role;

        if (!configId || role !== 'parent') {
          cascading = false;
          return _fetch.apply(null, [url, options]);
        }

        const addonKeys = [];
        for (const item of cart.items) {
          if (item === changedItem) continue;
          const p = item.properties || {};
          if (p._configuration_id === configId && p._role === 'addon') {
            addonKeys.push(item.key);
          }
        }

        if (addonKeys.length === 0) {
          cascading = false;
          return _fetch.apply(null, [url, options]);
        }

        const updates = {};
        updates[changedItem.key] = newQuantity;
        for (const key of addonKeys) updates[key] = newQuantity;

        const sectionsParam = body.sections || '';
        const sectionsUrl = body.sections_url || window.location.pathname;

        return _fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            updates,
            sections: sectionsParam,
            sections_url: sectionsUrl,
          }),
        });
      })
      .then(response => {
        cascading = false;
        return response;
      })
      .catch(err => {
        cascading = false;
        throw err;
      });
  };

  function observeCart() {
    const containers = document.querySelectorAll('cart-items-component');
    for (const container of containers) {
      if (container.dataset.jvObserved) continue;
      container.dataset.jvObserved = 'true';

      const observer = new MutationObserver(() => {
        clearTimeout(container._jvOrphanTimer);
        container._jvOrphanTimer = setTimeout(cleanOrphansFromCart, 500);
      });
      observer.observe(container, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeCart);
  } else {
    observeCart();
  }

  setInterval(observeCart, 3000);
})();
